import {
  APP_STORE_META,
  ELEMENTS_LOOKUP,
  ELEMENTS_META,
  GroupFields,
  GroupFieldsOptions,
  IElement,
  IRecipient,
  RecipientEntityTypeEnum,
  extractRecipientIdAndEntityName,
  getRecipientParamsFromContact,
  useTypography,
} from '@gohighlevel/ghl-proposals-common'
import { computed, toRaw } from 'vue'

import i18n from '@gohighlevel/ghl-proposals-common/src/locales'
import { useAppStore } from '@/store/app'
import { useBuilderStore } from '@/store/builder'
import { useElementOption } from '@/composable/elementOptions'

export const useRecipients = () => {
  const builderStore = useBuilderStore()
  const appStore = useAppStore()
  const { toCapitalize } = useTypography()
  const { updateDocumentOption, updateGroupOption, updateOption } =
    useElementOption()
  const recipients = computed(() => appStore.document?.recipients)

  const getCurrantLoggedInUserDetails = () => {
    const contactDetails = getRecipientParamsFromContact(
      appStore.userData,
      RecipientEntityTypeEnum.USERS,
      false
    )

    return {
      ...contactDetails,
      contactName: `${contactDetails.firstName} ${contactDetails.lastName}`,
    }
  }

  const signedByOptions = computed(() => {
    let allRecipients = recipients.value
    const currentUser = getCurrantLoggedInUserDetails()
    const currentUserExist = allRecipients.find(elem => {
      return (
        elem.id === currentUser.id &&
        elem.entityName === RecipientEntityTypeEnum.USERS
      )
    })
    if (!currentUserExist) {
      allRecipients = [...allRecipients, currentUser]
    }
    const recipientList =
      allRecipients.map((contact: IRecipient) => ({
        ...contact,
        label: toCapitalize(
          appStore.userId === contact.id
            ? `${contact?.contactName || ''} ${i18n.global.t('common.you')}`
            : contact?.contactName || `${contact.firstName} ${contact.lastName}`
        ).trim(),
        value: `${contact.id}_${contact.entityName}`,
      })) || []
    return recipientList
  })

  const assignRecipientToGroup = (val: string, groupId: string) => {
    const { recipientId, entityName } = extractRecipientIdAndEntityName(val)
    if (recipientId && entityName) {
      // For card save element business user is not allowed by payment widget
      // Check if group contains SaveCardDetails elements and prevent user assignment
      const group = builderStore.groups.find(g => g.id === groupId)
      const hasSaveCardDetailsElement = group?.children.some(
        ({ elementId }) => {
          const element = builderStore.getElementById(elementId)?.element
          return element?.type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS
        }
      )
      if (
        hasSaveCardDetailsElement &&
        entityName === RecipientEntityTypeEnum.USERS
      ) {
        return
      }
      if (
        entityName === RecipientEntityTypeEnum.USERS &&
        appStore.userId === recipientId
      ) {
        const isDuplicateUsers = recipients.value.find((el: any) => {
          return el.id === appStore.userId && el.entityName === entityName
        })
        if (!isDuplicateUsers) {
          const contactDetails = getCurrantLoggedInUserDetails()
          const allRecipients = [...recipients.value, contactDetails]
          updateDocumentOption(APP_STORE_META.RECIPIENTS, allRecipients)
        }
      }
      updateGroupOption(ELEMENTS_META.RECIPIENT, recipientId, groupId)
      updateGroupOption(ELEMENTS_META.ENTITY_NAME, entityName, groupId)

      // update group element also.
      group?.children.forEach(({ elementId }) => {
        const element = builderStore.getElementById(elementId)?.element
        if (element) {
          // For card save element business user is not allowed by payment widget
          // Skip assignment if element is SaveCardDetails and recipient is a user
          if (
            element.type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS &&
            entityName === RecipientEntityTypeEnum.USERS
          ) {
            return
          }
          updateOption(
            ELEMENTS_META.RECIPIENT,
            recipientId,
            element.id,
            element.type
          )
          updateOption(
            ELEMENTS_META.ENTITY_NAME,
            entityName,
            element.id,
            element.type
          )
        }
      })
    }
  }

  const assignRecipientToElement = (val: string, element: IElement) => {
    const id = element.id
    const type = element.type
    const { recipientId, entityName } = extractRecipientIdAndEntityName(val)
    if (recipientId && entityName) {
      // For card save element business user is not allowed by payment widget
      // Prevent assigning users to SaveCardDetails elements
      if (
        type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS &&
        entityName === RecipientEntityTypeEnum.USERS
      ) {
        return
      }
      if (
        entityName === RecipientEntityTypeEnum.USERS &&
        appStore.userId === recipientId
      ) {
        const isDuplicateUsers = recipients.value.find((el: any) => {
          return el.id === appStore.userId && el.entityName === entityName
        })
        if (!isDuplicateUsers) {
          const contactDetails = getCurrantLoggedInUserDetails()
          const allRecipients = [...recipients.value, contactDetails]
          updateDocumentOption(APP_STORE_META.RECIPIENTS, allRecipients)
        }
      }
    }
    updateOption(ELEMENTS_META.RECIPIENT, recipientId, id, type)
    updateOption(ELEMENTS_META.ENTITY_NAME, entityName, id, type)
  }

  const fetchRecipientForElementOrGroup = (
    element: IElement | GroupFields<GroupFieldsOptions>,
    recipientId?: string,
    entityName?: string
  ) => {
    const assignedRecipient = recipients.value.find((recipient: IRecipient) => {
      if (recipientId && entityName) {
        return (
          recipient.id === recipientId && recipient.entityName === entityName
        )
      } else {
        return (
          recipient.id ===
            element?.component?.options[ELEMENTS_META.RECIPIENT] &&
          recipient.entityName ===
            element?.component?.options[ELEMENTS_META.ENTITY_NAME]
        )
      }
    })
    return assignedRecipient
  }

  const getSelectValue = (
    element: IElement | GroupFields<GroupFieldsOptions>
  ) => {
    const recipientsList = toRaw(recipients.value)
    const lastSelectedId = element?.component.options[ELEMENTS_META.RECIPIENT]

    const userStillExistsInRecipients = recipientsList.find((el: any) => {
      return el.id === lastSelectedId
    })

    if (lastSelectedId && !userStillExistsInRecipients) {
      return null
    }

    if (
      element?.component?.options[ELEMENTS_META.RECIPIENT] &&
      element?.component?.options[ELEMENTS_META.ENTITY_NAME]
    ) {
      return `${element?.component?.options[ELEMENTS_META.RECIPIENT]}_${
        element?.component?.options[ELEMENTS_META.ENTITY_NAME]
      }`
    }
    return null
  }

  return {
    recipients,
    signedByOptions,
    getCurrantLoggedInUserDetails,
    assignRecipientToGroup,
    fetchRecipientForElementOrGroup,
    assignRecipientToElement,
    getSelectValue,
  }
}
